/**
 * Server notices and the new-version banner on the web (docs/redesign-2026-11/resilience.md §4):
 *
 * - Critical notices for `web` sit under the app bar on every page and can't be dismissed.
 * - The others sit at the top of Home; each can be dismissed on this device (remembered per notice id) unless the
 *   server says it can't.
 * - When the server asks for a newer web bundle than this one (`min_version.web.js` > `WEB_JS_VERSION`): "A new
 *   version of the site is ready · Reload".
 */
import { InfoIcon, WarningIcon, XIcon } from '@phosphor-icons/react'
import { activeNotices, webOutdated, type AppConfig, type AppNotice } from '@robotensor/api'
import { Link, useRouter } from '@tanstack/react-router'
import { useCallback, useEffect, useState, useSyncExternalStore, type ReactElement } from 'react'
import { useAppConfig, WEB_JS_VERSION } from '../lib/appConfig.ts'
import './notices.css'

export const DISMISSED_KEY = 'rt.notices.dismissed'

// ── Dismissals (per notice id, this device) ───────────────────────────────────────────────────────────────────

const dismissListeners = new Set<() => void>()
let dismissedCache: readonly string[] | null = null

export function readDismissed(storage: Storage | null = safeStorage()): readonly string[] {
  try {
    const v = JSON.parse(storage?.getItem(DISMISSED_KEY) ?? '[]') as unknown
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/** Remembers that `id` was dismissed here; critical notices are never dismissed. Keeps the newest 100 ids. */
export function dismissNotice(n: Pick<AppNotice, 'id' | 'tone' | 'dismissible'>, storage: Storage | null = safeStorage()): void {
  if (!canDismiss(n)) return
  const next = [...readDismissed(storage).filter((x) => x !== n.id), n.id].slice(-100)
  try {
    storage?.setItem(DISMISSED_KEY, JSON.stringify(next))
  } catch {
    // blocked storage: dismissed for this page view only
  }
  dismissedCache = next
  for (const l of dismissListeners) l()
}

export const canDismiss = (n: Pick<AppNotice, 'tone' | 'dismissible'>): boolean => n.tone !== 'critical' && n.dismissible !== false

function useDismissed(): readonly string[] {
  return useSyncExternalStore(
    (l) => {
      dismissListeners.add(l)
      return () => void dismissListeners.delete(l)
    },
    () => (dismissedCache ??= readDismissed()),
    () => [],
  )
}

/** What each place shows: the app bar's critical notices, Home's others (minus the dismissed ones). */
export function placeNotices(cfg: Pick<AppConfig, 'notices'>, dismissed: readonly string[], now = Date.now()): { bar: AppNotice[]; home: AppNotice[] } {
  const active = activeNotices(cfg, 'web', now)
  return {
    bar: active.filter((n) => n.tone === 'critical'),
    home: active.filter((n) => n.tone !== 'critical' && !(canDismiss(n) && dismissed.includes(n.id))),
  }
}

/** Re-renders each minute so notices start and end on time. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [])
  return now
}

// ── Parts ────────────────────────────────────────────────────────────────────────────────────────────────────

function NoticeLink({ link }: { link: string }): ReactElement | null {
  const router = useRouter()
  if (link.startsWith('/') && !link.startsWith('//')) {
    // an in-app link only when this build has the page; else the notice stays plain text
    if (!router.getMatchedRoutes(link.split(/[?#]/)[0] || '/')[2]) return null
    return (
      <Link to={link} className="w-notice__link">
        Open
      </Link>
    )
  }
  if (/^https:\/\//.test(link))
    return (
      <a href={link} className="w-notice__link" target="_blank" rel="noreferrer">
        Learn more
      </a>
    )
  return null
}

/** One notice: the tone's icon, title, body, an optional link, and × when it can be dismissed. */
export function NoticeCard({ n, onDismiss, preview }: { n: Pick<AppNotice, 'id' | 'tone' | 'title' | 'body' | 'link' | 'dismissible'>; onDismiss?: () => void; preview?: boolean }): ReactElement {
  const Icon = n.tone === 'info' ? InfoIcon : WarningIcon
  return (
    <div className="w-notice" data-tone={n.tone} role={n.tone === 'critical' ? 'alert' : 'status'} data-notice={n.id}>
      <Icon weight="fill" aria-hidden className="w-notice__icon" />
      <div className="w-notice__text">
        <b>{n.title}</b>
        {n.body ? <span>{n.body}</span> : null}
      </div>
      {n.link && !preview ? <NoticeLink link={n.link} /> : n.link ? <span className="w-notice__link">Open</span> : null}
      {canDismiss(n) && onDismiss ? (
        <button type="button" className="w-notice__x" aria-label={`Dismiss: ${n.title}`} onClick={onDismiss}>
          <XIcon weight="bold" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}

/** Under the app bar on every page: the new-version banner and critical notices. */
export function BarNotices(): ReactElement | null {
  const cfg = useAppConfig()
  const now = useMinute()
  const dismissed = useDismissed()
  const { bar } = placeNotices(cfg, dismissed, now)
  const outdated = webOutdated(cfg, WEB_JS_VERSION)
  const reload = useCallback(() => window.location.reload(), [])
  if (!outdated && bar.length === 0) return null
  return (
    <div className="w-barnotes">
      {outdated ? (
        <div className="w-notice" data-tone="update" role="status">
          <InfoIcon weight="fill" aria-hidden className="w-notice__icon" />
          <div className="w-notice__text">
            <b>A new version of the site is ready</b>
            {cfg.min_version.message ? <span>{cfg.min_version.message}</span> : null}
          </div>
          <button type="button" className="w-btn" data-size="sm" onClick={reload}>
            Reload
          </button>
        </div>
      ) : null}
      {bar.map((n) => (
        <NoticeCard key={n.id} n={n} />
      ))}
    </div>
  )
}

/** The top of Home: info and warning notices, dismissible on this device. */
export function HomeNotices(): ReactElement | null {
  const cfg = useAppConfig()
  const now = useMinute()
  const dismissed = useDismissed()
  const { home } = placeNotices(cfg, dismissed, now)
  if (!home.length) return null
  return (
    <section className="w-notices" aria-label="Notices">
      {home.map((n) => (
        <NoticeCard key={n.id} n={n} onDismiss={() => dismissNotice(n)} />
      ))}
    </section>
  )
}
